import type { Tbc, TbcPoint } from './tbc-types';
import { followsTangent } from './follow-tangent';
import { docDefaultParam, docDefaultWidthScale, type CrochetSizingDefaults } from './sizing-defaults';

/** Stable string id of a glyph part (e.g. 'leg', 'top-cap', 'crossbar'). */
export type PartId = string;
/** Stable string id of an adjustable scalar (e.g. 'crossbar', 'body', 'hook'). */
export type KnobId = string;

/** Separator joining a knob id to a leg id in a per-leg override key.
 *  Knob ids never contain it, so parsing splits on the FIRST occurrence and
 *  the remainder is the leg id (which may contain anything). */
export const KNOB_LEG_SEP = '@';

/** Build the params key for a per-leg knob override: `"<knobId>@<legId>"`. */
export function composeKnobKey(knobId: KnobId, legId: string): string {
    return `${knobId}${KNOB_LEG_SEP}${legId}`;
}

/** Inverse of composeKnobKey. A bare knob id (no separator) yields legId null. */
export function parseKnobKey(key: string): { knobId: KnobId; legId: string | null } {
    const i = key.indexOf(KNOB_LEG_SEP);
    if (i === -1) return { knobId: key, legId: null };
    return { knobId: key.slice(0, i), legId: key.slice(i + 1) };
}

/** Separator joining a knob id to a bar (crossbar part) id in a per-bar
 *  override key: `"<knobId>#<barId>"`. Distinct from KNOB_LEG_SEP so the two
 *  override tiers never collide. Bar ids are stitch-part ids and contain no `#`. */
export const KNOB_BAR_SEP = '#';

export function composeBarKey(knobId: KnobId, barId: string): string {
    return `${knobId}${KNOB_BAR_SEP}${barId}`;
}

export function parseBarKey(key: string): { knobId: KnobId; barId: string | null } {
    const i = key.indexOf(KNOB_BAR_SEP);
    if (i === -1) return { knobId: key, barId: null };
    return { knobId: key.slice(0, i), barId: key.slice(i + 1) };
}

/** Advanced (angle/position) offset knobs. Stored as offsets from baked-in
 *  geometry; absent ⇒ 0 ⇒ current look. Not part of any def's `knobs` list —
 *  surfaced into the resolved map by resolveKnobs so the geometry helpers and
 *  the length knobs can read them. */
export const ADVANCED_OFFSET_KEYS = ['capAngle', 'crossbarAngle', 'crossbarPos'] as const;

/** Per-bar-aware read of an advanced offset: per-bar override, else the
 *  stitch-master value, else 0. */
export function resolveBarValue(knobs: KnobValues, knobId: KnobId, barId: string): number {
    return knobs[composeBarKey(knobId, barId)] ?? knobs[knobId] ?? 0;
}

/** Resolved knob values for one stitch instance: knob id -> value. */
export type KnobValues = Record<KnobId, number>;

/** Resolved transform of one glyph leg. The glyph's TRANSFORM (driven by
 *  base/top points). Parts describe how the glyph is DRAWN from this frame;
 *  they never move/rotate/scale the whole glyph. */
export type LegFrame = {
    base: TbcPoint;
    top: TbcPoint;
    /** Unit vector along the leg (base -> top). */
    dir: TbcPoint;
    /** Unit vector perpendicular to the leg (dir rotated +90°). */
    perp: TbcPoint;
    legLength: number;
    /** Asymmetric classification of THIS leg's stitch, or null for symmetric.
     *  Set by stitch-handles / the renderer; legFrameFor leaves it undefined. */
    kind?: 'increase' | 'decrease' | null;
    /** Unit guide tangent at this leg's unique-end tip, when guide-snapped.
     *  Used to orient asymmetric `+` crossing bars; null/undefined → fall back
     *  to the leg perpendicular. */
    tipTangent?: TbcPoint | null;
    /** True when `tipTangent` is a decrease's apex-squaring FALLBACK
     *  (decreaseTopCapBarDir) rather than a resolved guide tangent. The
     *  fallback is a baseline the user's advanced capAngle offset rotates
     *  from, so resolveKnobs must NOT inert capAngle for it — only a real
     *  guide tangent takes the angle out of the user's hands. */
    tipTangentFallback?: boolean;
    /** The UN-padded base→top handle distance, set only when the frame was
     *  built from PADDED endpoints (so `legLength` — measured on the padded
     *  points — is shorter than the true handle distance). Post-stitch hook
     *  scaling reads this as the stretch-ratio numerator so a stitch at its
     *  natural handle distance renders the hook at exactly the stored value
     *  (ratio 1), independent of how much the top is padded. Absent ⇒ the frame
     *  was built from raw endpoints and `legLength` is already the raw distance. */
    rawLegLength?: number;
    /** Unit direction of a post-stitch increase's SHARED hook axis (base →
     *  centroid of the tops). The post-foot part, its handle, and its drag
     *  inverse all use this instead of `dir` when set, so the single hook sits
     *  square on the middle of the fan rather than tilting with whichever leg
     *  draws it. Absent ⇒ symmetric/decrease leg: the hook follows `dir`. */
    postFootDir?: TbcPoint | null;
    /** Set on a post-stitch INCREASE leg whose frame was re-aimed by
     *  aimPostIncreaseLeg so its stem leaves from the shared hook's END —
     *  where the loop meets `postFootDir` — like every leg of the chart
     *  symbol. `center` is the hook's center (the shared base, so the hook
     *  part and its handle center there rather than on this frame's `base`,
     *  which is then a virtual point on the stem's line); `stem` is that one
     *  point every leg's stem starts from. Absent ⇒ symmetric/decrease leg:
     *  the hook centers on `base` and the stem is cut at its radius along
     *  `dir`. */
    postIncreaseHook?: { center: TbcPoint; stem: TbcPoint } | null;
};

/** Renderer-agnostic draw primitive. Only the shapes needed so far are
 *  defined (dc uses segments); ellipse/arc are added when chain/hook are
 *  ported in a later plan. */
export type DrawSegment = { kind: 'segment'; a: TbcPoint; b: TbcPoint };
export type DrawPolyline = { kind: 'polyline'; points: TbcPoint[]; closed?: boolean };
export type DrawPrim = DrawSegment | DrawPolyline;

/** A handle the user can drag to change one knob, with its resolved position. */
export type KnobHandle = { knobId: KnobId; point: TbcPoint };

/** One drawable element of a stitch glyph; owns its own angle/anchor. */
export type StitchPart = {
    id: PartId;
    geometry(frame: LegFrame, knobs: KnobValues): {
        primitives: DrawPrim[];
        handles: KnobHandle[];
    };
};

/** An adjustable scalar belonging to a part. */
export type Knob = {
    id: KnobId;
    default: number;
    min: number;
    max: number;
    /** Where this knob's value is stored on the Tbc. 'params' (default) uses
     *  params[id]; 'widthScale' uses the shared Tbc.widthScale field (for the
     *  primary body width, shared with the rect path). */
    storage?: 'params' | 'widthScale';
    /** Position of the knob's drag handle for a given value. `knobs` is the
     *  fully-resolved value map for this leg (so a knob whose geometry depends
     *  on a sibling — e.g. an angle knob needing its bar's length/position —
     *  can read it). Length/width knobs ignore it. */
    handleAt(frame: LegFrame, value: number, knobs: KnobValues): TbcPoint;
    /** Recover the knob value from a pointer world position (inverse of
     *  handleAt). Implementations clamp to [min, max]. */
    valueFromDrag(frame: LegFrame, pointer: TbcPoint, knobs: KnobValues): number;
};

export type CrossbarDesc = {
    barId: string;
    lengthKnobId: string;
    baseFrac: number;
    /** Base direction the bar's angle offset rotates from: 'slash' (the 45°
     *  diagonal, default) or 'perp' (square across the leg — post single
     *  crochet's `+` bar). Absent ⇒ 'slash'. */
    baseDir?: 'slash' | 'perp';
};

export type StitchTypeDef = {
    type: string;
    parts: StitchPart[];
    knobs: Knob[];
    /** Crossbars eligible for angle/position adjustment (advanced sizing).
     *  Absent ⇒ none. */
    crossbars?: CrossbarDesc[];
    /** True if this stitch has a top cap eligible for cap-angle adjustment. */
    capBearing?: boolean;
};

/** Build the leg frame for a base/top pair. For coincident points the frame
 *  length is 0 and the direction defaults to +y so downstream math stays
 *  finite (matches the legacy renderer's early-return-on-zero contract:
 *  callers still guard legLength === 0 before drawing). */
export function legFrameFor(base: TbcPoint, top: TbcPoint): LegFrame {
    const dx = top.x - base.x;
    const dy = top.y - base.y;
    const legLength = Math.hypot(dx, dy);
    const ux = legLength === 0 ? 0 : dx / legLength;
    const uy = legLength === 0 ? 1 : dy / legLength;
    return {
        base,
        top,
        dir: { x: ux, y: uy },
        perp: { x: -uy, y: ux },
        legLength,
    };
}

const clampTo = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Resolve one knob: per-leg override (when legId given), else per-instance
 *  override, else the document's sizing default (clamped to the knob range),
 *  else the def default. `dd` is the owning document's sizingDefaults;
 *  undefined ⇒ factory behavior (pre-existing documents). */
export function resolveKnob(
    t: Tbc, def: StitchTypeDef, id: KnobId,
    legId: string | null, dd: CrochetSizingDefaults | undefined,
): number {
    const knob = def.knobs.find(k => k.id === id);
    if (knob == null) throw new Error(`unknown knob '${id}' for stitch type '${def.type}'`);
    if (legId != null) {
        const perLeg = t.params?.[composeKnobKey(id, legId)];
        if (perLeg != null) return perLeg;
    }
    const stored = knob.storage === 'widthScale' ? t.widthScale : t.params?.[id];
    if (stored != null) return stored;
    const doc = knob.storage === 'widthScale'
        ? docDefaultWidthScale(dd, t.type)
        : docDefaultParam(dd, t.type, id);
    if (doc != null) return clampTo(doc, knob.min, knob.max);
    return knob.default;
}

/** Resolve every knob of a def into a complete value map, honoring per-leg
 *  overrides when legId is given. `frame` (when the caller has one) supplies
 *  the resolved cap tangent so the capAngle inerting below can key off
 *  "actually following a tangent" rather than the follow flag alone. */
export function resolveKnobs(
    t: Tbc, def: StitchTypeDef,
    legId: string | null, dd: CrochetSizingDefaults | undefined,
    frame?: Pick<LegFrame, 'tipTangent' | 'tipTangentFallback'>,
): KnobValues {
    const out: KnobValues = {};
    for (const k of def.knobs) out[k.id] = resolveKnob(t, def, k.id, legId, dd);
    for (const id of ADVANCED_OFFSET_KEYS) {
        out[id] = t.params?.[id] ?? docDefaultParam(dd, t.type, id) ?? 0;
    }
    if (t.params != null) {
        for (const key of Object.keys(t.params)) {
            if (key.includes(KNOB_BAR_SEP)) out[key] = t.params[key]!;
        }
    }
    // Doc-default per-bar keys apply only when the stitch stored NEITHER that
    // bar key NOR the knob's master key — a dragged master handle overrides
    // the document's per-bar defaults (per-bar stitch > master stitch >
    // per-bar doc > master doc > 0; resolveBarValue reads the merged map).
    const ddParams = dd?.[t.type]?.params;
    if (ddParams != null) {
        for (const key of Object.keys(ddParams)) {
            if (!key.includes(KNOB_BAR_SEP)) continue;
            const { knobId } = parseBarKey(key);
            if (t.params?.[key] != null || t.params?.[knobId] != null) continue;
            out[key] = ddParams[key]!;
        }
    }
    // While the cap actually follows a resolved GUIDE tangent (following is on
    // AND the frame carries one), capAngle is not the user's to set (its
    // handle is hidden — see stitch-handles.ts capAngleLocked). Force it inert
    // here so EVERY geometry helper, handle position, and drag inverse (all of
    // which read through resolveKnobs) agrees, without touching the stored
    // value in params — it must survive so it reappears when the tangent goes
    // away (non-destructive, see follow-tangent.ts). A free-floating stitch has
    // nothing to follow, so its capAngle stays live even while following is on
    // (SP-28); a decrease's apex-squaring FALLBACK tangent
    // (frame.tipTangentFallback) is likewise only a BASELINE the stored angle
    // rotates from, not a lock. Callers with no frame in hand (tests,
    // previews) resolve as free-floating.
    if (followsTangent(t, dd) && frame?.tipTangent != null && frame.tipTangentFallback !== true) out['capAngle'] = 0;
    return out;
}

/** Build all draw primitives and knob handles for a stitch leg. When legId is
 *  given (asymmetric per-leg sizing), knobs resolve per-leg and each handle's
 *  knobId is namespaced to that leg via composeKnobKey, so the drag-commit can
 *  route it back to the right leg. */
export function buildStitchGeometry(
    t: Tbc,
    def: StitchTypeDef,
    frame: LegFrame,
    legId: string | null,
    dd: CrochetSizingDefaults | undefined,
): { primitives: DrawPrim[]; handles: KnobHandle[] } {
    const knobs = resolveKnobs(t, def, legId, dd, frame);
    const primitives: DrawPrim[] = [];
    const handles: KnobHandle[] = [];
    for (const part of def.parts) {
        const g = part.geometry(frame, knobs);
        primitives.push(...g.primitives);
        for (const h of g.handles) {
            handles.push(legId != null ? { ...h, knobId: composeKnobKey(h.knobId, legId) } : h);
        }
    }
    return { primitives, handles };
}
